import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { IconButton } from './Button';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  position?: 'bottom' | 'right' | 'left';
  footer?: React.ReactNode;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = 'bottom',
  footer,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const positionStyles = {
    bottom: 'inset-x-0 bottom-0 rounded-t-3xl max-h-[85vh] animate-in slide-in-from-bottom duration-300',
    right: 'inset-y-0 right-0 h-full w-full max-w-md rounded-l-3xl animate-in slide-in-from-right duration-300',
    left: 'inset-y-0 left-0 h-full w-full max-w-md rounded-r-3xl animate-in slide-in-from-left duration-300',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#111111]/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed bg-white border border-[#E5E9E9] shadow-2xl flex flex-col z-10 ${positionStyles[position]}`}
      >
        {position === 'bottom' && <div className="w-12 h-1.5 bg-[#E5E9E9] rounded-full mx-auto mt-3 mb-1" />}

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E9E9]">
          {title ? (
            <h3 className="text-base font-bold text-[#111111]">{title}</h3>
          ) : (
            <div />
          )}
          <IconButton icon={<X className="w-5 h-5" />} aria-label="Close drawer" onClick={onClose} size="sm" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>

        {/* Footer */}
        {footer && <div className="p-4 bg-[#F7FAFA] border-t border-[#E5E9E9] flex items-center justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
};
